import { useState } from "react";
import { Reorder } from "framer-motion";
import { Youtube, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { field, Label } from "../formBits";
import { SourceBrowser } from "./SourceBrowser";
import { AudioUploader } from "./AudioUploader";
import { QueueRow, findFull } from "./QueueRow";
import { QuickYouTube } from "./QuickYouTube";
import { SectionTracks } from "./SectionTracks";

const move = (arr, i, d) => {
  const j = i + d;
  if (j < 0 || j >= arr.length) return arr;
  const out = [...arr];
  [out[i], out[j]] = [out[j], out[i]];
  return out;
};

const MatchAll = ({ queue, setQueue }) => {
  const [busy, setBusy] = useState(null);
  const todo = queue.filter((t) => t.source === "spotify" && !t.video_id);
  if (!todo.length && !busy) return null;
  const run = async () => {
    let found = 0;
    const patches = {};
    for (let k = 0; k < todo.length; k++) {
      setBusy(`${k + 1}/${todo.length}`);
      try { patches[todo[k].id] = await findFull(todo[k]); found += 1; } catch { /* not found: stays a preview */ }
    }
    setQueue((q) => q.map((t) => (patches[t.id] ? { ...t, ...patches[t.id] } : t)));
    setBusy(null);
    toast.success(`FULL VERSIONS FOUND FOR ${found}/${todo.length} SPOTIFY TRACKS — SAVE TO PUBLISH`);
  };
  return (
    <button type="button" data-testid="music-match-all-btn" onClick={run} disabled={!!busy}
      className="flex items-center gap-2 px-4 py-2.5 border border-[#F5A524]/50 text-[#F5A524] font-mono text-[11px] tracking-[0.2em] hover:bg-[#F5A524] hover:text-black disabled:opacity-60">
      <Youtube size={14} /> {busy ? `SEARCHING ${busy}` : `PLAY ALL ${todo.length} SPOTIFY TRACKS IN FULL`}
    </button>
  );
};

export const TrackPicker = ({ s, update }) => {
  const queue = s.music_queue ?? [];
  const set = (k, v) => update((p) => ({ ...p, [k]: v }));
  const setQueue = (fn) => update((p) => ({ ...p, music_queue: typeof fn === "function" ? fn(p.music_queue ?? []) : fn }));
  const patch = (id, p) => setQueue((q) => q.map((t) => (t.id === id ? { ...t, ...p } : t)));
  const add = (tracks) => setQueue((q) => [...q, ...tracks]);

  return (
    <div className="space-y-6" data-testid="music-settings">
      <label className="flex items-center gap-3 cursor-pointer">
        <input data-testid="music-enabled-checkbox" type="checkbox" checked={!!s.music_enabled} onChange={(e) => set("music_enabled", e.target.checked)} className="w-4 h-4 accent-[#F5A524]" />
        <span className="font-mono text-xs tracking-[0.2em] text-zinc-300">SHOW THE MUSIC PLAYER ON MY SITE</span>
      </label>
      <label className="block"><Label>PLAYER TITLE (OPTIONAL)</Label>
        <input data-testid="music-title-input" value={s.music_title ?? ""} onChange={(e) => set("music_title", e.target.value)} placeholder="Soundtrack" className={field} />
      </label>

      <div className="space-y-4 border-t border-[#F5A524]/15 pt-5">
        <p className="font-mono text-xs tracking-[0.25em] text-[#F5A524]">01 — PICK TRACKS</p>
        <SourceBrowser url={s.music_url ?? ""} setUrl={(v) => set("music_url", v)} queue={queue} onAdd={add} />
        <QuickYouTube onAdd={add} />
        <AudioUploader onAdd={add} />
      </div>

      <div className="space-y-3 border-t border-[#F5A524]/15 pt-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="font-mono text-xs tracking-[0.25em] text-[#F5A524]">02 — YOUR QUEUE · <span data-testid="music-queue-count">{queue.length}</span> TRACKS</p>
          {queue.length > 0 && (
            <button type="button" data-testid="music-queue-clear" onClick={() => setQueue([])} className="flex items-center gap-1 font-mono text-[11px] tracking-[0.2em] text-zinc-400 hover:text-[#FF2A55]">
              <Trash2 size={12} /> CLEAR QUEUE
            </button>
          )}
        </div>
        {queue.length === 0 ? (
          <p data-testid="music-queue-empty" className="text-sm text-zinc-400 border border-dashed border-[#F5A524]/30 p-4">
            {s.music_url ? "Your queue is empty, so the site plays every track from the link above. Add tracks to choose exactly what plays, and in what order." : "Load a link or upload audio, then add tracks here."}
          </p>
        ) : (
          <>
            <p className="text-xs text-zinc-400">Drag the handle or use the arrows to set the order. The site plays top to bottom, then loops.</p>
            <MatchAll queue={queue} setQueue={setQueue} />
            <div data-lenis-prevent className="max-h-[520px] overflow-y-auto pr-1">
              <Reorder.Group axis="y" values={queue} onReorder={setQueue} layoutScroll className="space-y-2" data-testid="music-queue">
                {queue.map((t, i) => (
                  <QueueRow key={t.id} t={t} i={i} total={queue.length} onChange={(p) => patch(t.id, p)}
                    onMove={(d) => setQueue((q) => move(q, i, d))} onRemove={() => setQueue((q) => q.filter((x) => x.id !== t.id))} />
                ))}
              </Reorder.Group>
            </div>
          </>
        )}
      </div>
      <SectionTracks s={s} set={set} />
    </div>
  );
};
